/* eslint-disable react/no-array-index-key */
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Grid,
  Alert,
  Collapse,
  Badge,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Fade,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Check as CheckIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  TableView as TableViewIcon,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Button from "../primitives/Button";
import AutoCompleteDropdown from "../primitives/AutoCompleteDropdown";
import { useAlert } from "../../hook/useAlert";
import UploadProgressOverlay from "./UploadProgressOverlay";
import { useUser } from "../../context/UserContext";
import * as batchHelpers from "./utils/batchUpload";

const BatchUploadForm = () => {
  const theme = useTheme();
  const { user } = useUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { onInfo, onSuccess, onError } = useAlert();

  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [isValidated, setIsValidated] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [isDragging, setIsDragging] = useState(false);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [validationResult, setValidationResult] = useState(null); // { ok, data }
  const [previewOpen, setPreviewOpen] = useState(false);

  const fileSize = batchHelpers.formatFileSize(csvFile);
  const canProceed = selectedConfig && isValidated && csvData.length > 0;
  const isLoading = isLoadingConfigs || isValidating;
  const maxSizeInMB = 100;

  // Derived preview summary for partial failure messaging
  const previewSummary = validationResult?.summary;
  const partialWarningText =
    previewSummary?.hasFailures && !previewSummary?.allFailed
      ? `${previewSummary.failedCount} of ${previewSummary.totalPreview} preview records failed. You can proceed; only ${previewSummary.totalPreview - previewSummary.failedCount} will be ingested.`
      : "";

  const { handleDragOver, handleDragLeave, handleFileDrop } =
    batchHelpers.createDragHandlers(setIsDragging);

  const handleConfigSelect = (e) => {
    setSelectedConfig(e.target.value);
  };

  const processFile = async (file) => {
    const validation = batchHelpers.validateFile(file);
    if (validation.isValid) {
      setCsvFile(file);
      setIsValidated(false);
      setCsvData([]);
      setUploadStatus("idle");
      setValidationResult(null);

      setIsValidating(true);
      try {
        // 1) Preview validation first; always allow proceed, clarify sample size
        if (selectedConfig) {
          const result = await batchHelpers.validatePreview(selectedConfig, file);
          setValidationResult(result);
          const summary = result.summary || {};
          if (summary.allFailed || (result.ok === false && !result.summary)) {
            onError(
              `All ${summary.totalPreview || 0} preview records failed validation. Only a sample of 10 records is validated; you may proceed with upload, but only valid records will be ingested.`
            );
            // Do not return; allow upload
          } else if (summary.hasFailures) {
            onInfo(
              `${summary.failedCount || 0} of ${summary.totalPreview || 0} preview records failed validation. Only a sample of 10 records is validated; you may proceed with upload, but only valid records will be ingested.`
            );
          } else {
            onSuccess(
              `Validated sample of ${summary.totalPreview || 0} records successfully. Only a sample of 10 records is validated; you may proceed with upload, but only valid records will be ingested.`
            );
          }
        }

        // 2) Full CSV parse and success state
        const data = await batchHelpers.parseCsv(file);
        setCsvData(data);
        setIsValidated(true);
        onSuccess(`Validated ${data.length} records successfully`);
      } catch (error) {
        onError(error.message);
        setIsValidated(false);
        setCsvData([]);
      } finally {
        setIsValidating(false);
      }
    } else {
      onError(validation.error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
  };

  const handleFileDropWrapper = (e) => {
    handleFileDrop(e, processFile);
  };

  const removeFile = () => {
    setCsvFile(null);
    setCsvData([]);
    setIsValidated(false);
    setUploadStatus("idle");
    setValidationResult(null);
  };

  const handleUpload = async () => {
    setShowProgress(true);
    setUploadStatus("uploading");
    setUploadProgress(0);

    try {
      await batchHelpers.handleFileUpload(selectedConfig, csvFile, setUploadProgress);
      setUploadStatus("success");
      onSuccess("File uploaded successfully!");
    } catch (error) {
      setUploadStatus("error");
      onError(error.message);
    }
  };

  const handleReset = () => {
    setShowProgress(false);
    setSelectedConfig("");
    setCsvFile(null);
    setCsvData([]);
    setIsValidated(false);
    setUploadProgress(0);
    setUploadStatus("idle");
    setValidationResult(null);
  };

  useEffect(() => {
    const loadConfigs = async () => {
      if (!user?.region) return;

      setIsLoadingConfigs(true);
      try {
        const configsData = await batchHelpers.loadConfigurations(user.region);
        setConfigs(configsData);
        if (configsData.length === 0) {
          onInfo("No configurations available");
        }
      } catch (error) {
        onError("Failed to load configurations. Please try again.");
      } finally {
        setIsLoadingConfigs(false);
      }
    };
    loadConfigs();
    setSelectedConfig("");
  }, [user?.region]);

  if (showProgress) {
    return (
      <UploadProgressOverlay
        uploadStatus={uploadStatus}
        uploadProgress={uploadProgress}
        onReset={handleReset}
      />
    );
  }

  return (
    <Box>
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <AutoCompleteDropdown
          label="Select Partner Configuration"
          id="config-select"
          name="config"
          value={selectedConfig}
          onChange={handleConfigSelect}
          options={
            configs?.map((config) => ({
              value: config.id,
              label: config.id,
            })) || []
          }
          searchPlaceholder="Search configurations..."
        />
      </Card>
      {selectedConfig && (
        <Fade in timeout={300}>
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              CSV File Upload
            </Typography>
            {!csvFile ? (
              <Box
                sx={{
                  border: `2px dashed ${isDragging ? "#28a745" : "#ccc"}`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragging ? "#f8f9fa" : "transparent",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#28a745",
                    backgroundColor: "#f8f9fa",
                  },
                }}
                onDrop={handleFileDropWrapper}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => document.getElementById("fileInput").click()}
              >
                <TableViewIcon sx={{ fontSize: 48, color: "#28a745", mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Drag and drop your CSV file here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  or click to browse (max {maxSizeInMB}MB)
                </Typography>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  id="fileInput"
                />
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    border: "2px solid #28a745",
                    borderRadius: 2,
                    p: 3,
                    backgroundColor: "#f8f9fa",
                    mb: 3,
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid>
                      <Badge
                        badgeContent={isValidated ? <CheckIcon fontSize="small" /> : null}
                        color="success"
                        overlap="circular"
                      >
                        <TableViewIcon sx={{ fontSize: 40, color: "#28a745" }} />
                      </Badge>
                    </Grid>
                    <Grid size="grow">
                      <Typography variant="subtitle1">{csvFile.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {fileSize} •{" "}
                        {isValidating
                          ? "Validating..."
                          : isValidated
                            ? `${csvData?.length || 0} records`
                            : "Processing..."}
                      </Typography>
                    </Grid>
                    <Grid>
                      <IconButton onClick={removeFile} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
                {isValidating && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Validating file...</Typography>
                  </Box>
                )}
                {isValidated && validationResult?.ok === true && (
                  <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
                    File validated successfully! {csvData.length} records ready for upload.
                    {validationResult?.data?.mappedRecord?.createProspect && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setPreviewOpen(true)}
                          name="Preview First Record"
                        />
                      </Box>
                    )}
                  </Alert>
                )}
                {previewSummary?.hasFailures && !previewSummary?.allFailed && (
                  <Alert severity="warning" sx={{ mb: 2 }} icon={<InfoOutlinedIcon />}>
                    {partialWarningText}
                    {validationResult?.data?.mappedRecord?.createProspect && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setPreviewOpen(true)}
                          name="Preview First Record"
                        />
                      </Box>
                    )}
                  </Alert>
                )}
                {(validationResult?.summary?.allFailed ||
                  (validationResult &&
                    validationResult.ok === false &&
                    !validationResult.summary)) && (
                  <Alert severity="error" sx={{ mb: 2 }} icon={<InfoOutlinedIcon />}>
                    All preview records in the sample failed validation.
                    <br />
                    Only a sample of 10 records is validated; you may proceed with upload, but only
                    valid records will be ingested.
                  </Alert>
                )}
                {validationResult &&
                  validationResult.ok === false &&
                  validationResult.data?.failedRecords?.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Failed Preview Records
                      </Typography>
                      <TableContainer
                        sx={{ maxHeight: 320, border: "1px solid #e0e0e0", borderRadius: 1 }}
                      >
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              {Object.keys(
                                validationResult.data.failedRecords[0]?.record || {}
                              ).map((key) => (
                                <TableCell
                                  key={key}
                                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                                >
                                  {key}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {validationResult.data.failedRecords.map((fr, idx) => {
                              const errorFields = new Set(
                                (fr.errors || []).map((e) =>
                                  (e.instancePath || "").replace(/^\//, "")
                                )
                              );
                              return (
                                <TableRow key={idx} hover>
                                  {Object.entries(fr.record).map(([key, value], cidx) => {
                                    const hasError = errorFields.has(key);
                                    const errMsg = (fr.errors || [])
                                      .filter((e) => (e.instancePath || "").slice(1) === key)
                                      .map((e) => e.message)
                                      .join("; ");
                                    return (
                                      <TableCell
                                        key={`${idx}-${cidx}`}
                                        sx={{
                                          backgroundColor: hasError ? "#ffebee" : undefined,
                                          borderColor: hasError ? "#ef9a9a" : undefined,
                                          borderWidth: hasError ? 1 : undefined,
                                          borderStyle: hasError ? "solid" : undefined,
                                        }}
                                        title={hasError ? errMsg : ""}
                                      >
                                        {value?.toString?.() ?? String(value)}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
              </Box>
            )}
          </Card>
        </Fade>
      )}
      {csvData?.length > 0 && validationResult?.ok !== false && (
        <Collapse in={isValidated}>
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Data Preview
            </Typography>
            {isMobile ? (
              <Box>
                <Alert severity="info" icon={<CheckCircleIcon />}>
                  <strong>{csvData.length} records</strong> loaded successfully
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Preview available on larger screens
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  First 5 Records
                </Typography>
                <TableContainer
                  sx={{ maxHeight: 300, border: "1px solid #e0e0e0", borderRadius: 1 }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {Object.keys(csvData[0] || {}).map((key) => (
                          <TableCell
                            key={key}
                            sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                          >
                            {key}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {csvData.slice(0, 5).map((row, index) => (
                        <TableRow key={index} hover>
                          {Object.values(row).map((value, colIndex) => (
                            <TableCell key={colIndex}>{value?.toString() || ""}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Card>
        </Collapse>
      )}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4 }}>
        {selectedConfig && csvFile && (
          <Button variant="outlined" onClick={removeFile} name="Clear" />
        )}
        <Button
          variant="contained"
          color="success"
          onClick={handleUpload}
          disabled={!canProceed || isLoading}
          startIcon={<UploadIcon />}
          name="Upload File"
        />
      </Box>
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Preview: createProspect (first successful record)</DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              p: 2,
              backgroundColor: "#f9f9f9",
              borderRadius: 1,
              border: "1px solid #eee",
              maxHeight: 400,
              overflow: "auto",
            }}
          >
            {JSON.stringify(validationResult?.data?.mappedRecord?.createProspect || {}, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setPreviewOpen(false)} name="Close" />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchUploadForm;
